import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import { MarketplaceDisclaimer } from '@/components/MarketplaceDisclaimer';
import { BRAND, IMAGES } from '@/data/constants';
import { FULL_DISCLAIMER, ROLE_RESPONSIBILITIES, TERMS_VERSION, biList } from '@/data/legal';
// Section copy lives in ONE module so the build-time crawler prerender
// (src/prerender/compliancePages.ts) publishes exactly the same text.
import { LEGAL_CONTENT } from '@/data/legalContent';

export const LegalPage: React.FC<{ kind: 'privacy' | 'terms' }> = ({ kind }) => {
  const { t, lang } = useI18n();
  const sections = LEGAL_CONTENT[kind][lang === 'es' ? 'es' : 'en'];
  const title = kind === 'privacy' ? t('legal.privacyTitle') : t('legal.termsTitle');
  const roleKeys: Array<'homeowner' | 'gc' | 'subcontractor'> = ['homeowner', 'gc', 'subcontractor'];

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <Seo title={title} description={`${title} — ${BRAND.fullName}`} />
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
      <p className="mt-2 text-sm text-slate-400">
        {t('legal.updated')}: {new Date().toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US')} · v{TERMS_VERSION}
      </p>

      <MarketplaceDisclaimer variant="full" className="mt-6" />

      <div className="mt-8 space-y-8">
        {sections.map(([h, body]) => (
          <section key={h}>
            <h2 className="text-lg font-extrabold text-slate-900">{h}</h2>
            <p className="mt-2 leading-relaxed text-slate-600">{body}</p>
          </section>
        ))}

        {kind === 'terms' && (
          <section>
            <h2 className="text-lg font-extrabold text-slate-900">
              {lang === 'es' ? 'Responsabilidades de verificación por tipo de usuario' : 'Verification responsibilities by user type'}
            </h2>
            <div className="mt-3 grid gap-4 sm:grid-cols-3">
              {roleKeys.map((r) => (
                <div key={r} className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-sm font-bold text-slate-900">
                    {lang === 'es' ? ROLE_RESPONSIBILITIES[r].title.es : ROLE_RESPONSIBILITIES[r].title.en}
                  </p>
                  <ul className="mt-2 list-disc pl-5 text-sm text-slate-600">
                    {(lang === 'es' ? ROLE_RESPONSIBILITIES[r].items.es : ROLE_RESPONSIBILITIES[r].items.en).map((i) => (
                      <li key={i}>{i}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-2">
              {biList(FULL_DISCLAIMER, lang).map((p) => (
                <p key={p.slice(0, 20)} className="text-sm leading-relaxed text-slate-600">
                  {p}
                </p>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};


export const HowItWorks: React.FC = () => {
  const { t, path } = useI18n();
  const steps = [1, 2, 3].map((i) => ({ title: t(`home.how${i}Title`), desc: t(`home.how${i}Desc`) }));

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <Seo title={t('home.howTitle')} description={t('home.heroSub')} />
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{t('home.howTitle')}</h1>
      <p className="mt-3 max-w-2xl text-slate-500">{t('home.heroSub')}</p>
      <img src={IMAGES.hero} alt="" className="mt-8 h-56 w-full rounded-2xl object-cover sm:h-72" />
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {steps.map((s, i) => (
          <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-6">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-sm font-extrabold text-white">
              {i + 1}
            </span>
            <h2 className="mt-4 text-lg font-bold text-slate-900">{s.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 flex flex-wrap gap-3">
        <Link to={path('/register')} className="rounded-xl bg-orange-500 px-6 py-3.5 text-base font-bold text-white hover:bg-orange-600">
          {t('home.getStarted')}
        </Link>
        <Link to={path('/jobs')} className="rounded-xl border border-slate-300 px-6 py-3.5 text-base font-bold text-slate-800 hover:bg-slate-50">
          {t('home.ctaFindWork')}
        </Link>
      </div>
    </div>
  );
};

export default LegalPage;
